/**
 * Cálculo de vencimentos de requisições de compra para projeção de fluxo de caixa.
 * Espelha backend/src/utils/paymentCalculator.js
 */

import { parseDateOnly } from './dateUtils'

export function getRequisitionTotalValue(req) {
  const invoice = parseFloat(req?.invoice_total)
  if (Number.isFinite(invoice) && invoice > 0) return invoice
  return parseFloat(req?.total_value ?? req?.totalValue) || 0
}

export function calculateInstallmentsLocal(totalValue, installments, firstDueDate, intervalDays = 30) {
  if (!totalValue || totalValue <= 0) {
    throw new Error('Valor total deve ser maior que zero')
  }
  if (!installments || installments < 1) {
    throw new Error('Número de parcelas inválido')
  }
  if (!firstDueDate) {
    throw new Error('Data do primeiro vencimento é obrigatória')
  }

  const interval = intervalDays || 30
  const firstDate = parseDateOnly(firstDueDate)
  if (!firstDate) {
    throw new Error('Data do primeiro vencimento inválida')
  }

  const installmentAmount = Math.floor((totalValue / installments) * 100) / 100
  const remainder = Math.round((totalValue - installmentAmount * installments) * 100) / 100
  const result = []

  for (let i = 1; i <= installments; i += 1) {
    const dueDate = new Date(firstDate)
    dueDate.setDate(dueDate.getDate() + (i - 1) * interval)

    let amount = installmentAmount
    if (i === installments && remainder !== 0) {
      amount = installmentAmount + remainder
    }

    const y = dueDate.getFullYear()
    const m = String(dueDate.getMonth() + 1).padStart(2, '0')
    const d = String(dueDate.getDate()).padStart(2, '0')

    result.push({
      installmentNumber: i,
      dueDate: `${y}-${m}-${d}`,
      amount: Math.round(amount * 100) / 100
    })
  }

  return result
}

function formatTodayISO() {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Monta cronograma de saídas a partir da requisição ou parcelas salvas (getById).
 */
export function buildRequisitionPaymentSchedule(req) {
  const total = getRequisitionTotalValue(req)
  if (total <= 0) {
    return { schedule: [], ready: false, reason: 'Sem valor informado' }
  }

  if (Array.isArray(req.payments) && req.payments.length > 0) {
    const schedule = req.payments
      .map((p, index) => ({
        installmentNumber: p.installment_number ?? p.installmentNumber ?? index + 1,
        dueDate: String(p.due_date ?? p.dueDate ?? '').split('T')[0],
        amount: parseFloat(p.amount) || 0
      }))
      .filter((p) => p.dueDate && p.amount > 0)

    if (schedule.length > 0) {
      return { schedule, ready: true, source: 'saved' }
    }
  }

  const method = req.payment_method
  if (!method) {
    return { schedule: [], ready: false, reason: 'Defina a forma de pagamento na aprovação' }
  }

  if (method === 'cash') {
    const dueRaw = req.payment_first_due_date
    const dueDate = dueRaw ? String(dueRaw).split('T')[0] : formatTodayISO()
    return {
      schedule: [{ installmentNumber: 1, dueDate, amount: total }],
      ready: true,
      source: 'cash'
    }
  }

  if (method === 'installments') {
    const count = parseInt(req.payment_installments, 10) || 1
    const firstDue = req.payment_first_due_date
    const interval = parseInt(req.payment_interval_days, 10) || 30

    if (!firstDue) {
      return { schedule: [], ready: false, reason: 'Informe o 1º vencimento na aprovação' }
    }

    try {
      const schedule = calculateInstallmentsLocal(total, count, firstDue, interval)
      return { schedule, ready: true, source: 'calculated' }
    } catch (err) {
      return { schedule: [], ready: false, reason: err.message || 'Erro ao calcular parcelas' }
    }
  }

  return { schedule: [], ready: false, reason: 'Forma de pagamento não suportada' }
}

/**
 * Agrega saídas de várias requisições selecionadas.
 * @returns {Array<{ date: string, amount: number, requisitionId: number, title: string }>}
 */
export function aggregatePurchaseOutflows(requisitions) {
  const events = []

  requisitions.forEach((req) => {
    const { schedule, ready } = buildRequisitionPaymentSchedule(req)
    if (!ready) return

    schedule.forEach((inst) => {
      events.push({
        date: inst.dueDate,
        amount: inst.amount,
        requisitionId: req.id,
        title: req.title || `Pedido #${req.id}`
      })
    })
  })

  return events
}
